// @system — what the legal pages (Terms, Privacy, Cookies) read from the product identity.
//
// brand.json `legal` (written by Orkosi provisioning, passed through by scripts/apply-brand.js into
// config/@custom/info.js) carries: effectiveDate (ISO or free text), country (the business's country,
// for the governing-law clause), status ('template' until the business or its first legal task
// reviewed the text, 'reviewed' afterwards). A site provisioned without the block is a template:
// the pages say so and show no effective date they never had (dev-rankradar, 2026-10-12: "Effective
// April 19, 2026" on a site created that day, no governing country, broken template sentences).

export const LEGAL_STATUS_TEMPLATE = 'template'
export const LEGAL_STATUS_REVIEWED = 'reviewed'

/** @typedef {{ name?: string, supportEmail?: string, legalEffectiveDate?: string, legalCountry?: string, legalStatus?: string }} LegalInfo */

/** @param {LegalInfo} info */
export function legalStatus(info) {
  return info && info.legalStatus === LEGAL_STATUS_REVIEWED ? LEGAL_STATUS_REVIEWED : LEGAL_STATUS_TEMPLATE
}

/** Is the legal text still the unreviewed template? @param {LegalInfo} info */
export function isLegalTemplate(info) {
  return legalStatus(info) === LEGAL_STATUS_TEMPLATE
}

/**
 * The "Effective <date>" line, or '' when no date was ever set (never an invented one).
 * @param {LegalInfo} info
 * @param {string} [label]
 */
export function effectiveDateLine(info, label = 'Effective') {
  const raw = String((info && info.legalEffectiveDate) || '').trim()
  if (!raw) return ''
  const d = /^\d{4}-\d{2}-\d{2}/.test(raw) ? new Date(raw) : null
  const text = d && !Number.isNaN(d.getTime())
    ? d.toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })
    : raw
  return `${label} ${text}`
}

/** The governing-law sentence: the business's country when known, never a guessed jurisdiction. @param {LegalInfo} info */
export function governingLawSentence(info) {
  const country = String((info && info.legalCountry) || '').trim()
  const name = String((info && info.name) || 'the Service').trim()
  if (country) {
    return `These Terms are governed by the laws of ${country}, without regard to conflict of law provisions. Any dispute arising under these Terms is brought before the competent courts of ${country}.`
  }
  return `These Terms are governed by the laws of the country where ${name} is established, without regard to conflict of law provisions. Any dispute arising under these Terms is brought before the competent courts of that country.`
}

/**
 * Where "contact us" goes: the support mailbox when the business has one, else the contact page.
 * @param {LegalInfo} info
 * @returns {{ href: string, label: string, isEmail: boolean }}
 */
export function contactLink(info) {
  const email = String((info && info.supportEmail) || '').trim()
  if (email) return { href: `mailto:${email}`, label: email, isEmail: true }
  return { href: '/contact', label: 'our contact page', isEmail: false }
}
